import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { apiRequest, getToken } from "../api";
import { sitePath, siteResolveQuery, useSiteSlug } from "../site-context";

type Booking = {
  id: string;
  status: string;
  preferredStartsAt: string;
  addressLine1: string;
  city: string;
  region: string;
  service?: { name: string };
};

export function SitePortalPage() {
  const { slug: paramSlug = "" } = useParams();
  const slug = useSiteSlug(paramSlug);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const qs = siteResolveQuery(slug, true);
    if (!getToken() || (!qs.includes("slug=") && !qs.includes("host="))) return;
    void apiRequest<{ site: { business: { id: string; name: string } } }>(
      `/sites/resolve?${qs}`,
    )
      .then(async (data) => {
        setBusinessId(data.site.business.id);
        setBusinessName(data.site.business.name);
        const list = await apiRequest<{ bookings: Booking[] }>(
          `/businesses/${data.site.business.id}/portal/bookings`,
        );
        setBookings(list.bookings);
      })
      .catch((err: Error) => setError(err.message));
  }, [slug]);

  if (!getToken()) {
    return <Navigate to={sitePath(slug, "login")} replace />;
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Client portal</p>
          <h1>Your bookings with {businessName || "…"}</h1>
        </div>
        <div className="cta-row">
          <Link className="btn btn-primary" to={sitePath(slug, "book")}>
            Book a Service
          </Link>
          <Link className="btn btn-secondary" to={sitePath(slug)}>
            Website
          </Link>
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section className="panel" style={{ padding: "1.25rem" }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>When</th>
              <th>Location</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>{booking.service?.name ?? "—"}</td>
                <td>{new Date(booking.preferredStartsAt).toLocaleString()}</td>
                <td>
                  {booking.addressLine1}
                  <div className="notice">
                    {booking.city}, {booking.region}
                  </div>
                </td>
                <td>{booking.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && businessId ? (
          <p className="notice">No bookings yet.</p>
        ) : null}
      </section>
    </div>
  );
}
